import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { apiGet } from '../lib/api';
import { useCart } from '../context/CartContext';

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [attempts, setAttempts] = useState(0);
  const [failed, setFailed] = useState(false);
  const { clear } = useCart();
  const nav = useNavigate();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setFailed(true);
      return;
    }
    const tick = async () => {
      try {
        const res = await apiGet(`/payments/status/${sessionId}`);
        if (res.payment_status === 'paid' && res.order_number) {
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          nav(`/order/${res.order_number}`, { replace: true });
          return true;
        }
      } catch {}
      return false;
    };
    const timer = setInterval(async () => {
      setAttempts((a) => a + 1);
      if (await tick()) {
        clearInterval(timer);
        return;
      }
      if (attempts > 40) {
        clearInterval(timer);
        setFailed(true);
      }
    }, 2000);
    tick();
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, attempts]);

  return (
    <div className="bg-forest noise relative min-h-screen grid place-items-center px-4">
      <div className="max-w-md w-full bg-cream rounded-3xl p-10 text-center shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)]" data-testid="payment-success-pending">
        {failed ? (
          <>
            <h1 className="font-display font-extrabold uppercase text-forest text-3xl">Still processing</h1>
            <p className="text-sm text-sub mt-3 mb-6">
              Your payment may have completed but we're still confirming it. Check your account's order
              history in a minute — or contact us and we'll sort it.
            </p>
            <div className="space-y-3">
              <Link to="/account" className="block bg-forest hover:bg-flame text-cream font-display font-semibold uppercase tracking-wide px-6 py-3.5 rounded-full transition-colors">Check Order History</Link>
              <Link to="/contact" className="block border-2 border-forest text-forest hover:bg-forest hover:text-cream font-display font-semibold uppercase tracking-wide px-6 py-3.5 rounded-full transition-colors">Contact Support</Link>
            </div>
          </>
        ) : (
          <>
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }} className="mx-auto w-12 h-12 text-flame mb-6">
              <Loader2 className="w-12 h-12" />
            </motion.div>
            <h1 className="font-display font-extrabold uppercase text-forest text-3xl">Confirming your payment…</h1>
            <p className="text-sm text-sub mt-3">Hang tight — Stripe is confirming your card. This takes a few seconds.</p>
          </>
        )}
      </div>
    </div>
  );
}
